'use client';

import React, { type ReactNode } from 'react';
import NextLink from 'next/link';
import classNames from 'classnames';
import { useGetHelpAndSupport } from '@/common/apis/personal-pages/help-and-support';
import Button from '@/common/components/button';
import Loading from '@/common/components/loading';
import Text from '@/common/components/text';
import { appConfigs } from '@/common/configs/app.config';
import ChevLeft from '~/public/assets/images/svg/arrow-left.svg';
import MessageOnline from '~/public/assets/images/svg/message-line.svg';
import SupportCall from '~/public/assets/images/svg/shop-phone.svg';
import VideoPlayer from '~/public/assets/images/svg/video-player.svg';
import CustomerSupportLink from './customer-support-link';

interface Props {
  title: string;
  description?: string;
  handleToggle?: () => void;
  hasWitroMagLink?: boolean;
  wrapperClassname?: string;
  hideIcons?: boolean;
  hideButton?: boolean;
  isTenant: boolean;
}
export interface Link {
  icon: ReactNode;
  title: string;
  description?: ReactNode;
  href: string;
  target?: string;
}

interface OfficeTimes {
  title: string;
  description: string | null;
}

const links: Link[] = [
  {
    icon: <MessageOnline />,
    title: 'گفتگوی آنلاین',
    href: 'https://goftino.com/c/IFBb0S',
  },
  {
    icon: <SupportCall />,
    title: 'تماس با پشتیبانی',
    description: (
      <div className="flex flex-col items-end justify-center gap-1">
        {/* <NextLink href={`tel:${appConfigs.supportPhoneNumber}`}>
          {appConfigs.supportIranianPhoneNumber}
        </NextLink> */}
        <NextLink href={`tel:${appConfigs.supportPhoneNumber2}`}>
          {appConfigs.supportIranianPhoneNumber2}
        </NextLink>
      </div>
    ),
    href: ``,
  },
];

const witroMagLink: Link[] = [
  {
    icon: <VideoPlayer />,
    title: 'مشاهده آموزش گام به گام',
    href: `${appConfigs.witroMagUrl}%d9%81%d8%b1%d8%a7%d8%ae%d9%88%d8%a7%d9%86%db%8c-%d8%a7%d8%b2-%d8%a7%db%8c%d9%86%d8%b3%d8%aa%d8%a7%da%af%d8%b1%d8%a7%d9%85/`,
    target: '_blank',
  },
];

export default function CustomerSupportDrawerContent({
  description,
  title,
  handleToggle,
  hasWitroMagLink,
  wrapperClassname,
  hideButton = false,
  hideIcons = false,
  isTenant,
}: Props) {
  const { data, isLoading } = useGetHelpAndSupport({ enabled: isTenant });

  const witroOfficeTimes: OfficeTimes[] = [
    {
      title: 'ساعت کاری شنبه تا چهارشنبه:',
      description: '08 صبح الی 12 شب',
    },
    {
      title: 'ساعت کاری روزهای تعطیل:',
      description: '08 صبح الی 16 بعدازظهر',
    },
  ];
  const getTenantOfficeTime = (): OfficeTimes[] => {
    if (!data) return [];

    const {
      isFulltimeHelpAndSupportEnabled,
      supportTimeWorkingDays,
      supportTimeHolidays,
    } = data.data;

    if (isFulltimeHelpAndSupportEnabled) {
      return [
        {
          title: 'ساعت کاری پشتیبانی:',
          description: '۷ روز هفته و ۲۴ ساعت روز کنارتونیم!',
        },
      ];
    }
    if (supportTimeHolidays.length && supportTimeWorkingDays) {
      return [
        {
          title: 'زمان پشتیبانی روزهای کاری:',
          description: supportTimeWorkingDays,
        },
        {
          title: 'زمان پشتیبانی روزهای تعطیل:',
          description: supportTimeHolidays,
        },
      ];
    }

    if (supportTimeHolidays.length) {
      return [
        {
          title: 'زمان پشتیبانی روزهای تعطیل:',
          description: supportTimeHolidays,
        },
      ];
    }
    if (supportTimeWorkingDays.length) {
      return [
        {
          title: 'زمان پشتیبانی روزهای کاری:',
          description: supportTimeWorkingDays,
        },
      ];
    }

    return [];
  };

  const tenantOfficeTime = getTenantOfficeTime();

  if (isTenant && isLoading) {
    return <Loading />;
  }
  return (
    <div className={classNames('w-full', wrapperClassname)}>
      {title.length ? (
        <Text className="border-b border-silver-200 pb-4 text-sm font-medium text-secondary-300">
          {title}
        </Text>
      ) : (
        <></>
      )}

      {(description?.length ?? 0) > 0 && (
        <Text className="mb-6 mt-3 text-sm font-medium text-black-300">
          {description}
        </Text>
      )}

      <div className="flex flex-col gap-y-3">
        {!isTenant ? (
          links.map((link) => (
            <CustomerSupportLink
              link={link}
              key={link.title}
              hideIcon={hideIcons}
            />
          ))
        ) : (
          <NextLink
            href={`/contact-us`}
            onClick={handleToggle}
            className="w-full flex justify-between items-center border border-blue-500 rounded-xl px-3 py-4"
          >
            <span className="text-black-default font-medium text-sm">
              تماس با پشتیبانی
            </span>
            <ChevLeft />
          </NextLink>
        )}

        {hasWitroMagLink &&
          witroMagLink.map((link) => (
            <CustomerSupportLink
              link={link}
              key={link.title}
              hideIcon={hideIcons}
            />
          ))}

        {!isTenant &&
          witroOfficeTimes.map((time) => (
            <div
              className="mb-1 flex h-11 items-center justify-between rounded-md bg-coconut-default px-4 pb-1"
              key={time.title}
            >
              <Text className="pt-1 text-xs font-medium">{time.title}</Text>

              <Text className="pt-1 text-xs font-semibold">
                {time.description}
              </Text>
            </div>
          ))}
        {isTenant &&
          tenantOfficeTime.map((time) => (
            <div
              className="mb-1 flex flex-col items-start md:flex-row px-4 py-2 md:items-center gap-2 md:gap-4 justify-between rounded-md bg-coconut-default p-4"
              key={time.title}
            >
              <Text className="pt-1 text-xs font-medium whitespace-nowrap md:whitespace-normal w-1/3">
                {time.title}
              </Text>

              <Text className="pt-1 text-xs font-semibold w-2/3">
                {time.description}
              </Text>
            </div>
          ))}
        {!hideButton ? (
          <Button
            type="black"
            onClick={handleToggle}
            className="mt-3 h-11 text-xs font-medium lg:mb-5"
          >
            نیاز به پشتیبانی ندارم
          </Button>
        ) : (
          <></>
        )}
      </div>
    </div>
  );
}
